import { useState } from "react";
import { useTranslation } from "react-i18next";
import { panelThemeClasses } from "../../../theme/themeClasses";
import { Button, EmptyState, SectionHeader, confirmDelete, notifyError, notifySuccess } from "../../../components/ui";
import { useCostProfiles } from "../../../features/costProfiles/queries";
import { useDeleteCostProfile } from "../../../features/costProfiles/mutations";
import type { CostProfile } from "../../../features/costProfiles/types";
import { useDisableCRUD } from "../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../components/base/CRUDTooltip";
import { CostProfileCard } from "../components/CostProfileCard";
import { CostProfileModal } from "../components/CostProfileModal";

type ModalState = { mode: "create"; seed?: CostProfile | null } | { mode: "edit"; profile: CostProfile } | null;

/** Perfiles de costos operativos por clase de unidad. */
export default function CostosSection() {
  const { t } = useTranslation("finanzasOperativas");
  const themeClasses = panelThemeClasses();
  const disableCRUD = useDisableCRUD();
  const query = useCostProfiles();
  const delMut = useDeleteCostProfile();
  const [modal, setModal] = useState<ModalState>(null);

  const profiles = query.data ?? [];

  const remove = async (p: CostProfile) => {
    const ok = await confirmDelete({ title: t("costProfiles.confirmDelete.title"), text: t("costProfiles.confirmDelete.text") });
    if (!ok) return;
    try {
      await delMut.mutateAsync(p.id);
      void notifySuccess(t("costProfiles.toasts.deleted"));
    } catch (err) {
      void notifyError(t("costProfiles.toasts.error"), (err as Error)?.message);
    }
  };

  return (
    <div className={`flex h-full w-full flex-col ${themeClasses.bg}`}>
      <SectionHeader
        title={t("sections.costos.title")}
        subtitle={t("sections.costos.subtitle")}
        actions={
          <CRUDTooltip>
            <Button size="sm" icon={<i className="ri-add-line" />} disabled={disableCRUD} onClick={() => setModal({ mode: "create" })}>
              {t("sections.costos.new")}
            </Button>
          </CRUDTooltip>
        }
      />
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {query.isLoading ? (
          <div className="flex min-h-64 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          </div>
        ) : query.isError ? (
          <EmptyState icon="ri-error-warning-line" title={t("sections.costos.error.title")} action={<Button size="sm" onClick={() => void query.refetch()}>{t("common.retry")}</Button>} />
        ) : profiles.length === 0 ? (
          <EmptyState
            icon="ri-calculator-line"
            title={t("sections.costos.empty.title")}
            description={t("sections.costos.empty.description")}
            action={
              !disableCRUD ? (
                <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setModal({ mode: "create" })}>
                  {t("sections.costos.new")}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {profiles.map((p) => (
              <CostProfileCard
                key={p.id}
                profile={p}
                disableCRUD={disableCRUD}
                onEdit={() => setModal({ mode: "edit", profile: p })}
                onDuplicate={() => setModal({ mode: "create", seed: p })}
                onDelete={() => void remove(p)}
              />
            ))}
          </div>
        )}
      </div>

      {modal && (
        <CostProfileModal
          profile={modal.mode === "edit" ? modal.profile : null}
          seed={modal.mode === "create" ? modal.seed ?? null : null}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
